import { ComponentRef, ElementRef, Injectable, Renderer2 } from '@angular/core'
import { FieldPlaceholderComponent } from '../fields/field-placeholder.component'
import * as lodash from 'lodash'
import { FieldAction, FieldsEditorActionManagerService } from './fields-editor-action-manager.service'
import { SignFieldRect } from '@tokeet-frontend/signature'

type FieldComponentRef = ComponentRef<FieldPlaceholderComponent>

interface Point {
  x: number
  y: number
}

class Rect {
  p1: Point
  p2: Point

  constructor(p1?: Point, p2?: Point) {
    this.p1 = p1
    this.p2 = p2
  }

  isInteracted(rect: Rect) {
    return !(
      rect.topLeftPoint.x > this.bottomRightPoint.x ||
      rect.bottomRightPoint.x < this.topLeftPoint.x ||
      rect.topLeftPoint.y > this.bottomRightPoint.y ||
      rect.bottomRightPoint.y < this.topLeftPoint.y
    )
  }

  get x() {
    return this.topLeftPoint.x
  }

  get y() {
    return this.topLeftPoint.y
  }

  get topLeftPoint() {
    return { x: Math.min(this.p1.x, this.p2.x), y: Math.min(this.p1.y, this.p2.y) }
  }

  get bottomRightPoint() {
    return { x: Math.max(this.p1.x, this.p2.x), y: Math.max(this.p1.y, this.p2.y) }
  }

  get width() {
    return Math.abs(this.p2.x - this.p1.x)
  }

  get height() {
    return Math.abs(this.p2.y - this.p1.y)
  }
}

export enum FieldsAlignment {
  Left,
  Right,
  Top,
  Bottom,
}

/**
 * this service is for aligning fields. and should not be singleton
 * */
@Injectable()
export class FieldsAlignManagerService {
  private fields = new Set<FieldComponentRef>()

  private containerRef: ElementRef<HTMLDivElement>
  private get container(): HTMLDivElement {
    return this.containerRef?.nativeElement
  }

  private rectElmRef: ElementRef<HTMLDivElement>
  private get selectedBoxDiv(): HTMLDivElement {
    return this.rectElmRef?.nativeElement
  }

  selectedBoxRect: Rect
  selectedFields: FieldComponentRef[]
  isResizing = false

  mouseDownHandler = (e: MouseEvent) => (this.isFieldsEditor(e) ? this.onMouseDown(e) : this.reset())
  mouseMoveHandler = (e: MouseEvent) => this.onMouseMove(e)
  mouseUpHandler = (e: MouseEvent) => this.onMouseUp(e)

  constructor(private render: Renderer2, private actionManager: FieldsEditorActionManagerService) {}

  init(elmRef: ElementRef<HTMLDivElement>, rectElmRef: ElementRef<HTMLDivElement>) {
    this.containerRef = elmRef
    this.rectElmRef = rectElmRef
    setTimeout(() => {
      this.mountEvents()
    }, 2000)
  }

  destroy() {
    this.unmountEvents()

    this.containerRef = null
  }

  addField(field: FieldComponentRef) {
    this.fields.add(field)
  }

  removeField(field: FieldComponentRef) {
    this.fields.delete(field)
  }

  private isFieldSelected(field: FieldComponentRef): boolean {
    const fieldRect = field.instance.getFieldRect()
    return this.selectedBoxRect.isInteracted(
      new Rect(
        { x: fieldRect.x, y: fieldRect.y },
        { x: fieldRect.x + fieldRect.width, y: fieldRect.y + fieldRect.height }
      )
    )
  }

  getSelectedFields() {
    if (!this.selectedBoxRect) {
      return
    }
    const select: FieldComponentRef[] = []
    this.fields.forEach((t) => {
      if (this.isFieldSelected(t)) {
        select.push(t)
      }
    })

    return select
  }

  private isFieldsEditor(event: MouseEvent) {
    return lodash.toLower((event.target as HTMLElement).tagName) === 'canvas'
  }

  private mountEvents() {
    this.container.addEventListener('mousedown', this.mouseDownHandler, false)
    this.container.addEventListener('mousemove', this.mouseMoveHandler, false)
    this.container.addEventListener('mouseup', this.mouseUpHandler, false)
  }

  private unmountEvents() {
    this.container.removeEventListener('mousedown', this.mouseDownHandler)
    this.container.removeEventListener('mousemove', this.mouseMoveHandler)
    this.container.removeEventListener('mouseup', this.mouseUpHandler)
  }

  private onMouseDown(event: MouseEvent) {
    this.reset()
    this.isResizing = true
    const parentRect = this.container.getBoundingClientRect()
    this.selectedBoxRect = new Rect({
      x: event.clientX - parentRect.left,
      y: event.clientY - parentRect.top,
    })
  }

  private onMouseMove(event: MouseEvent) {
    if (!this.isResizing) {
      return
    }

    if (!this.selectedBoxRect) {
      // no mouse down
      return
    }
    const parentRect = this.container.getBoundingClientRect()
    this.selectedBoxRect.p2 = {
      x: event.clientX - parentRect.left,
      y: event.clientY - parentRect.top,
    }
    this.refresh()
  }

  private onMouseUp(event: MouseEvent) {
    this.isResizing = false
    if (!this.selectedBoxRect?.p2) {
      // no mouse move, click event
      this.reset()
      return
    }
    const parentRect = this.container.getBoundingClientRect()
    this.selectedBoxRect.p2 = {
      x: event.clientX - parentRect.left,
      y: event.clientY - parentRect.top,
    }

    this.resizeSelectedBox()
    this.refresh()
  }

  private resizeSelectedBox() {
    const fields = this.getSelectedFields()
    this.selectedBoxRect = lodash.reduce(
      fields,
      (rect: Rect, field: FieldComponentRef) => {
        const r = field.instance.getFieldRect()

        const p1: Point = { x: r.x, y: r.y }
        const p2: Point = { x: r.x + r.width, y: r.y + r.height }

        if (!rect.p1 || !rect.p2) {
          rect.p1 = p1
          rect.p2 = p2
          return rect
        }

        // top left
        rect.p1.x = p1.x > rect.p1.x ? rect.p1.x : p1.x
        rect.p1.y = p1.y > rect.p1.y ? rect.p1.y : p1.y

        // bottom right
        rect.p2.x = p2.x < rect.p2.x ? rect.p2.x : p2.x
        rect.p2.y = p2.y < rect.p2.y ? rect.p2.y : p2.y

        return rect
      },
      new Rect()
    )
  }

  reset() {
    this.resetHighlightedSelectedFields()
    this.resetSelectedBox()
  }

  private refresh() {
    if (!this.selectedBoxRect?.p1 || !this.selectedBoxRect?.p2) {
      this.reset()
      return
    }
    this.renderSelectedBox()
    this.resetHighlightedSelectedFields()
    this.highlightSelectedFields()
  }

  private renderSelectedBox() {
    const box: HTMLDivElement = this.selectedBoxDiv
    const select = this.selectedBoxRect
    this.render.addClass(box, 'active')
    if (!this.isResizing) {
      this.render.addClass(box, 'resize-end')
    }
    if (select.y < 50) {
      this.render.addClass(box, 'actions-bottom')
    } else {
      this.render.removeClass(box, 'actions-bottom')
    }

    this.render.setStyle(box, 'left', `${select.x}px`)
    this.render.setStyle(box, 'top', `${select.y}px`)
    this.render.setStyle(box, 'width', `${select.width}px`)
    this.render.setStyle(box, 'height', `${select.height}px`)
  }

  private resetSelectedBox() {
    this.render.removeClass(this.selectedBoxDiv, 'active')
    this.render.removeClass(this.selectedBoxDiv, 'resize-end')
    this.selectedBoxRect = null
  }

  private highlightSelectedFields() {
    const selected = this.getSelectedFields()
    if (!selected?.length) {
      return
    }
    this.selectedFields = selected
    lodash.forEach(selected, (f) => {
      this.render.addClass(f.location.nativeElement, 'field-selected')
    })
  }

  private resetHighlightedSelectedFields() {
    const selected = this.selectedFields
    if (!selected?.length) {
      return
    }
    lodash.forEach(selected, (f) => {
      this.render.removeClass(f.location.nativeElement, 'field-selected')
    })
  }

  doAlign(alignment: FieldsAlignment) {
    if (!this.selectedFields?.length) {
      return
    }
    let actions: FieldAction[] = lodash.map(this.selectedFields, (field): FieldAction => {
      const item = field.instance
      const rect: SignFieldRect = item.getFieldRect()
      let newFieldRect: SignFieldRect
      switch (alignment) {
        case FieldsAlignment.Left:
          newFieldRect = { ...rect, x: this.selectedBoxRect.p1.x }
          break
        case FieldsAlignment.Right:
          newFieldRect = { ...rect, x: this.selectedBoxRect.p2.x - rect.width }
          break
        case FieldsAlignment.Top:
          newFieldRect = { ...rect, y: this.selectedBoxRect.p1.y }
          break
        case FieldsAlignment.Bottom:
          newFieldRect = { ...rect, y: this.selectedBoxRect.p2.y - rect.height }
          break
      }
      if (item.isPositionWillChanged(newFieldRect)) {
        item.doMove(newFieldRect)
        return { fieldId: item.id, type: 'move', before: rect, after: newFieldRect }
      }
    })
    actions = actions.filter((a) => !!a)
    this.actionManager.batch(actions)
  }
}
